import { useState } from "react";
import { Sparkles, Wand2, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "../context/AuthContext";
import { toast } from "sonner";

const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const field =
  "w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-2 text-[#0A1128] text-sm";

const currency = new Intl.NumberFormat("pt-PT", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
});

const AiPricingPanel = ({ houses, onHousesRefresh }) => {
  // Quick estimator
  const [form, setForm] = useState({
    location: "",
    month: new Date().getMonth() + 1,
    bedrooms: 2,
    bathrooms: 1,
    max_guests: 4,
    base_price: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Auto-tune (per house)
  const [tuning, setTuning] = useState(null); // house id being tuned

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        location: form.location,
        month: Number(form.month),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        max_guests: Number(form.max_guests),
      };
      if (form.base_price) payload.base_price = Number(form.base_price);
      const { data } = await api.post("/admin/price-suggest", payload);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "A IA não respondeu.");
    } finally {
      setLoading(false);
    }
  };

  const autoTune = async (h) => {
    setTuning(h.id);
    try {
      const { data } = await api.post(`/admin/houses/${h.id}/auto-tune`);
      toast.success("Multiplicadores mensais gerados pela IA ✨");
      await onHousesRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no auto-tune.");
    } finally {
      setTuning(null);
    }
  };

  const toggleAi = async (h) => {
    try {
      await api.post(`/admin/houses/${h.id}/ai-pricing?enabled=${!h.ai_pricing_enabled}`);
      toast.success(h.ai_pricing_enabled ? "IA desativada" : "IA ativada");
      await onHousesRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-12">
      {/* 1. Quick Estimator */}
      <section className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 md:p-8" data-testid="ai-estimator">
        <div className="flex items-center gap-3">
          <Sparkles size={18} strokeWidth={1.4} className="text-[#C5A059]" />
          <h3 className="font-serif-display text-2xl md:text-3xl font-light text-[#0A1128]">
            Calculadora inteligente de preço
          </h3>
        </div>
        <p className="mt-2 text-sm text-[#3A435E] font-light max-w-2xl">
          Diga a localização e o mês — a IA devolve o valor ideal por noite, equilibrando ocupação e receita,
          com base no mercado real de alojamento local em Portugal.
        </p>

        <form onSubmit={submit} className="mt-6 grid md:grid-cols-3 gap-4" data-testid="ai-estimator-form">
          <input required placeholder="Localização (ex: Cascais, Portugal)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={`${field} md:col-span-2`} data-testid="ai-field-location" />
          <select value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className={field} data-testid="ai-field-month">
            {MONTHS_PT.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <input type="number" min={1} placeholder="Quartos" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className={field} data-testid="ai-field-bedrooms" />
          <input type="number" min={1} placeholder="WC" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className={field} data-testid="ai-field-bathrooms" />
          <input type="number" min={1} placeholder="Hóspedes máx." value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: e.target.value })} className={field} data-testid="ai-field-guests" />
          <input type="number" min={0} placeholder="Preço base atual (€, opcional)" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} className={`${field} md:col-span-2`} data-testid="ai-field-base" />
          <button type="submit" disabled={loading} className="md:col-span-1 bg-[#0A1128] text-white hover:bg-[#162243] px-6 py-3 text-xs tracking-[0.22em] uppercase flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ai-submit">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loading ? "A pensar…" : "Sugerir preço"}
          </button>
        </form>

        {result && (
          <div className="mt-8 border-t border-[#0A1128]/10 pt-8 grid md:grid-cols-12 gap-8 items-start" data-testid="ai-result">
            <div className="md:col-span-5">
              <div className="overline">Preço sugerido · {result.month_name}</div>
              <div className="font-serif-display text-6xl md:text-7xl font-light text-[#C5A059] mt-3" data-testid="ai-result-price">
                {currency.format(result.price)}
              </div>
              <div className="mt-4 text-sm text-[#3A435E]">
                Intervalo ótimo: <span className="text-[#0A1128]">{currency.format(result.min)} – {currency.format(result.max)}</span>
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="overline mb-3">Análise</div>
              <p className="text-[#0A1128] leading-relaxed text-sm">{result.reasoning}</p>
            </div>
          </div>
        )}
      </section>

      {/* 2. Per-house auto-tune */}
      <section data-testid="ai-auto-tune">
        <div className="flex items-center gap-3 mb-2">
          <Wand2 size={18} strokeWidth={1.4} className="text-[#C5A059]" />
          <h3 className="font-serif-display text-2xl md:text-3xl font-light text-[#0A1128]">
            Pricing dinâmico por casa
          </h3>
        </div>
        <p className="text-sm text-[#3A435E] font-light max-w-2xl">
          A IA gera 12 multiplicadores mensais por casa. Ao ativar, o preço da reserva ajusta-se automaticamente
          ao mês do check-in (verão mais caro, época baixa mais barato).
        </p>

        <div className="mt-6 space-y-4">
          {houses.map((h) => (
            <div key={h.id} className="bg-[#FCFAF8] border border-[#0A1128]/10 p-5" data-testid={`ai-house-${h.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="overline text-[#C5A059]">{h.location}</div>
                  <div className="font-serif-display text-lg text-[#0A1128]">{h.name}</div>
                  <div className="text-xs text-[#3A435E] mt-1">
                    Base €{Math.round(h.nightly_price || 0)}/noite · {h.bedrooms}q · {h.max_guests}pax
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {h.ai_pricing_enabled && (
                    <span className="text-xs uppercase tracking-[0.2em] text-[#2D6A4F] flex items-center gap-1.5">
                      <CheckCircle2 size={14} /> IA ativa
                    </span>
                  )}
                  <button
                    onClick={() => autoTune(h)}
                    disabled={tuning === h.id || !h.nightly_price}
                    className="bg-[#C5A059] text-[#0A1128] hover:bg-[#DCC287] px-4 py-2 text-[10px] tracking-[0.22em] uppercase disabled:opacity-40 flex items-center gap-1.5"
                    data-testid={`ai-tune-${h.id}`}
                  >
                    {tuning === h.id ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                    {tuning === h.id ? "A afinar…" : (h.monthly_multipliers?.length ? "Reajustar IA" : "Gerar com IA")}
                  </button>
                  {h.monthly_multipliers?.length > 0 && (
                    <button
                      onClick={() => toggleAi(h)}
                      className="text-xs uppercase tracking-[0.18em] text-[#0A1128] hover:text-[#C5A059]"
                      data-testid={`ai-toggle-${h.id}`}
                    >
                      {h.ai_pricing_enabled ? "Desativar" : "Ativar"}
                    </button>
                  )}
                </div>
              </div>

              {h.monthly_multipliers?.length === 12 && (
                <div className="mt-5 pt-4 border-t border-[#0A1128]/10">
                  <div className="overline mb-2">Preço por mês</div>
                  <div className="grid grid-cols-6 md:grid-cols-12 gap-1 text-center">
                    {h.monthly_multipliers.map((m, i) => {
                      const price = Math.round((h.nightly_price || 0) * m);
                      const hot = m > 1.15;
                      const cold = m < 0.9;
                      return (
                        <div
                          key={`${h.id}-m${i + 1}`}
                          className={`p-2 ${hot ? "bg-[#C5A059]/20" : cold ? "bg-[#0A1128]/10" : "bg-white"} border border-[#0A1128]/10`}
                          data-testid={`ai-month-${h.id}-${i + 1}`}
                        >
                          <div className="text-[9px] tracking-[0.18em] uppercase text-[#3A435E]">
                            {MONTHS_PT[i].slice(0, 3)}
                          </div>
                          <div className="font-serif-display text-sm text-[#0A1128] mt-1">€{price}</div>
                          <div className="text-[9px] text-[#3A435E]/70">×{m.toFixed(2)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AiPricingPanel;
